import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { ChevronDown, Plus, Download, FileText, XCircle, Check, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeesPaymentsPage() {
  // State management
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showReversal, setShowReversal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const { toast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    amount: '',
    paymentDate: '',
    paymentMethod: 'Bank Transfer', // Bank Transfer, Cash, Check
    termId: '',
    academicYear: new Date().getFullYear(),
    receiptFile: null,
  });

  const [reversalData, setReversalData] = useState({
    reason: '',
  });

  // Filter state
  const [filters, setFilters] = useState({
    status: '', // All, Pending, Paid, Reversed, Cancelled
    student: '',
    term: '',
    dateFrom: '',
    dateTo: '',
  });

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      // Build query params from filters
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.student) queryParams.append('studentName', filters.student);
      if (filters.term) queryParams.append('termId', filters.term);
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);

      const response = await fetch(`${API}/fees-payments?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch payments');
      const data = await response.json();
      setPayments(Array.isArray(data) ? data : (Array.isArray(data?.payments) ? data.payments : []));
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast({ title: 'Error', description: 'Failed to load payments' });
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.student, filters.term, filters.dateFrom, filters.dateTo, toast]);

  // Fetch payments on component mount and whenever filters change
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.studentId || !formData.amount || !formData.paymentDate) {
      toast({ title: 'Error', description: 'Please fill all required fields' });
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      toast({ title: 'Error', description: 'Amount must be greater than 0' });
      return;
    }

    try {
      const body = new FormData();
      body.append('studentId', formData.studentId);
      body.append('amount', formData.amount);
      body.append('paymentDate', formData.paymentDate);
      body.append('paymentMethod', formData.paymentMethod);
      body.append('termId', formData.termId);
      body.append('academicYear', formData.academicYear);
      if (formData.receiptFile) {
        body.append('receiptFile', formData.receiptFile);
      }

      const response = await fetch(`${API}/fees-payments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body,
      });

      if (!response.ok) throw new Error('Failed to record payment');

      toast({ title: 'Success', description: 'Payment recorded successfully' });
      setShowAddPayment(false);
      setFormData({
        studentId: '',
        studentName: '',
        amount: '',
        paymentDate: '',
        paymentMethod: 'Bank Transfer',
        termId: '',
        academicYear: new Date().getFullYear(),
        receiptFile: null,
      });
      fetchPayments();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast({ title: 'Error', description: error.message });
    }
  };

  const handleReversePayment = async (e) => {
    e.preventDefault();

    if (!reversalData.reason.trim()) {
      toast({ title: 'Error', description: 'Please provide a reason for reversal' });
      return;
    }

    try {
      const response = await fetch(`${API}/fees-payments/${selectedPayment.id}/reverse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ reason: reversalData.reason }),
      });

      if (!response.ok) throw new Error('Failed to reverse payment');

      toast({ title: 'Success', description: 'Payment reversed successfully' });
      setShowReversal(false);
      setSelectedPayment(null);
      setReversalData({ reason: '' });
      fetchPayments();
    } catch (error) {
      console.error('Error reversing payment:', error);
      toast({ title: 'Error', description: error.message });
    }
  };

  const handleUploadReceipt = async (paymentId, file) => {
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('receiptFile', file);

      const response = await fetch(`${API}/fees-payments/${paymentId}/upload-receipt`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to upload receipt');

      toast({ title: 'Success', description: 'Receipt uploaded successfully' });
      fetchPayments();
    } catch (error) {
      console.error('Error uploading receipt:', error);
      toast({ title: 'Error', description: error.message });
    }
  };

  const formatPaymentDate = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const normalized = String(status || '').toLowerCase();
    const badgeClass = {
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-amber-100 text-amber-800',
      paid: 'bg-green-100 text-green-800',
      'fully paid': 'bg-green-100 text-green-800',
      reversed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return badgeClass[normalized] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const normalized = String(status || '').toLowerCase();
    switch (normalized) {
      case 'paid':
      case 'fully paid':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'reversed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'partial':
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getDisplayStatus = (payment) => {
    const status = String(payment?.status || payment?.fee_status || payment?.paymentStatus || '').trim();
    if (status) return status;

    const totalFee = Number(payment?.total_fee || 0);
    const amountPaid = Number(payment?.amount_paid || 0);
    if (totalFee > 0 && amountPaid >= totalFee) return 'Fully Paid';
    if (amountPaid > 0) return 'Partial';
    return 'Pending';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Fee Payments</h1>
          <p className="text-gray-600 mt-1">Record and manage student fee payments</p>
        </div>
        <Button onClick={() => setShowAddPayment(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Record Payment
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Input
              placeholder="Student name"
              value={filters.student}
              onChange={(e) => setFilters({ ...filters, student: e.target.value })}
            />
            <select
              className="border rounded px-3 py-2"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Partial">Partial</option>
              <option value="Paid">Fully Paid</option>
              <option value="Reversed">Reversed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              placeholder="From date"
            />
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              placeholder="To date"
            />
            <Button onClick={fetchPayments} className="bg-blue-600 hover:bg-blue-700">
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add Payment Modal */}
      {showAddPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle>Record Fee Payment</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddPayment} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Student ID/Name *
                    </label>
                    <Input
                      placeholder="Search student"
                      value={formData.studentId}
                      onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount (USD) *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Date *
                    </label>
                    <Input
                      type="date"
                      value={formData.paymentDate}
                      onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Method
                    </label>
                    <select
                      className="w-full border rounded px-3 py-2"
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    >
                      <option>Bank Transfer</option>
                      <option>Cash</option>
                      <option>Check</option>
                      <option>Mobile Money</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Term
                    </label>
                    <select
                      className="w-full border rounded px-3 py-2"
                      value={formData.termId}
                      onChange={(e) => setFormData({ ...formData, termId: e.target.value })}
                    >
                      <option value="">Select term</option>
                      <option value="1">Term 1</option>
                      <option value="2">Term 2</option>
                      <option value="3">Term 3</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Academic Year
                    </label>
                    <Input
                      type="number"
                      value={formData.academicYear}
                      onChange={(e) => setFormData({ ...formData, academicYear: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Receipt File
                  </label>
                  <Input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => setFormData({ ...formData, receiptFile: e.target.files[0] })}
                  />
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddPayment(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    Record Payment
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reversal Modal */}
      {showReversal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Reverse Payment</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleReversePayment} className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Student: <strong>{selectedPayment.studentName}</strong>
                  </p>
                  <p className="text-sm text-gray-600 mb-4">
                    Amount: <strong>${parseFloat(selectedPayment.amount).toFixed(2)}</strong>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Reversal *
                  </label>
                  <textarea
                    className="w-full border rounded px-3 py-2"
                    rows="4"
                    placeholder="Enter reason for reversing this payment"
                    value={reversalData.reason}
                    onChange={(e) => setReversalData({ reason: e.target.value })}
                  />
                </div>

                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800">
                    ⚠️ This action will reverse the payment and update the student's balance.
                    An audit trail will be recorded.
                  </p>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowReversal(false);
                      setSelectedPayment(null);
                      setReversalData({ reason: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-red-600 hover:bg-red-700">
                    Reverse Payment
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle>Fee Payments History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading payments...</p>
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No payments found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Student</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Grade</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Amount (USD)</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Method</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Reference</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => {
                    const displayStatus = getDisplayStatus(payment);
                    return (
                    <tr key={payment.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">{formatPaymentDate(payment.paymentDate || payment.payment_date)}</td>
                      <td className="py-3 px-4">{payment.studentName || payment.studentFullName || `${payment.first_name || ''} ${payment.last_name || ''}`.trim() || '—'}</td>
                      <td className="py-3 px-4">{payment.studentGrade || '—'}</td>
                      <td className="py-3 px-4 font-semibold">${Number(payment.amount || 0).toFixed(2)}</td>
                      <td className="py-3 px-4 text-sm">{payment.paymentMethod || payment.payment_method || '—'}</td>
                      <td className="py-3 px-4 text-sm">{payment.receiptNumber || payment.receipt_number || payment.payment_reference || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(displayStatus)}
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(displayStatus)}`}>
                            {displayStatus}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          {payment.receiptFilePath || payment.receipt_file ? (
                            <button
                              onClick={() => window.open(payment.receiptFilePath || payment.receipt_file)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Download receipt"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          ) : (
                            <label className="text-blue-600 hover:text-blue-800 cursor-pointer" title="Upload receipt">
                              <FileText className="w-4 h-4" />
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.png,.jpg,.jpeg"
                                onChange={(e) => handleUploadReceipt(payment.id, e.target.files[0])}
                              />
                            </label>
                          )}
                          {(displayStatus === 'Paid' || displayStatus === 'Fully Paid') && (
                            <button
                              onClick={() => {
                                setSelectedPayment(payment);
                                setShowReversal(true);
                              }}
                              className="text-red-600 hover:text-red-800"
                              title="Reverse payment"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {payments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-sm">Total Payments</p>
              <p className="text-2xl font-bold text-gray-900">
                ${payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-sm">Fully Paid</p>
              <p className="text-2xl font-bold text-green-600">
                {payments.filter((p) => getDisplayStatus(p) === 'Paid' || getDisplayStatus(p) === 'Fully Paid').length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <p className="text-gray-600 text-sm">Pending / Partial</p>
              <p className="text-2xl font-bold text-yellow-600">
                {payments.filter((p) => ['Pending', 'Partial'].includes(getDisplayStatus(p))).length}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
